/**
 * CompteBancairePanel — Visualise et gère les entrées/sorties d'argent.
 *
 * Agrège automatiquement les saisies de :
 *  - Trésorerie (entrées/sorties manuelles)
 *  - Factures (ventes = entrées, achats = sorties)
 *  - Investisseurs (mises = entrées)
 *
 * Permet aussi d'ajouter, modifier et supprimer directement des mouvements.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  ArrowLeft, Landmark, TrendingUp, TrendingDown, Wallet, ArrowDownLeft, ArrowUpRight,
  Plus, Pencil, Trash2, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/,/g, ' ');
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch { return iso; }
};
const today = () => new Date().toISOString().split('T')[0];

const SOURCE_BADGE = {
  tresorerie: { label: 'Trésorerie', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  facture: { label: 'Facture', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  investisseur: { label: 'Apport', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const CATEGORIES_CREDIT = [
  'Ventes - Locations panneaux', 'Ventes - Production', 'Ventes - Services',
  'Apport investisseur', 'Subvention', 'Remboursement', 'Divers'
];
const CATEGORIES_DEBIT = [
  'Achats fournisseurs', 'Investissements - Panneaux', 'Salaires', 'Marketing',
  'Loyer', 'Impôts & taxes', 'Frais bancaires', 'Énergie', 'Transport', 'Maintenance', 'Divers'
];

export default function CompteBancairePanel({ onBack }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => currentYear - i);
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState({ mouvements: [], total_entrees: 0, total_sorties: 0, solde_courant: 0 });
  const [loading, setLoading] = useState(true);

  // Modaux Ajout / Édition
  const [editEntry, setEditEntry] = useState(null);     // null | { mode: 'add'|'edit', sens: 'credit'|'debit', ... }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/financial/compte-bancaire`, { params: { annee: year } });
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

  const grouped = useMemo(() => {
    const groups = {};
    (data.mouvements || []).forEach((m) => {
      const month = (m.date || '').slice(0, 7) || 'sans-date';
      if (!groups[month]) groups[month] = [];
      groups[month].push(m);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [data.mouvements]);

  const monthLabel = (ym) => {
    if (!ym || ym === 'sans-date') return 'Sans date';
    try {
      const [y, m] = ym.split('-');
      return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    } catch { return ym; }
  };

  // ── Actions ──
  const openAdd = (sens) => {
    setEditEntry({
      mode: 'add', sens,
      date: today(),
      description: '',
      categorie: sens === 'credit' ? CATEGORIES_CREDIT[0] : CATEGORIES_DEBIT[0],
      montant: '',
    });
  };

  const openEdit = (m) => {
    if (m.source !== 'tresorerie') {
      toast.info(`Cette ligne provient de ${SOURCE_BADGE[m.source]?.label}. Modifiez-la depuis sa source.`);
      return;
    }
    setEditEntry({
      mode: 'edit', sens: m.type, id: m.id,
      date: m.date, description: m.description, categorie: m.categorie, montant: m.montant,
    });
  };

  const submitEntry = async () => {
    if (!editEntry) return;
    const { mode, sens, id, date, description, categorie, montant } = editEntry;
    const amt = parseFloat(montant);
    if (!amt || amt <= 0) { toast.error('Montant invalide'); return; }
    if (!date) { toast.error('Date requise'); return; }
    if (!description?.trim()) { toast.error('Description requise'); return; }

    const payload = { date, description: description.trim(), categorie, montant: amt };
    try {
      if (mode === 'add') {
        await axios.post(`${API}/api/financial/${sens}`, payload);
        toast.success(sens === 'credit' ? 'Entrée ajoutée' : 'Sortie ajoutée');
      } else {
        await axios.put(`${API}/api/financial/${sens}/${id}`, payload);
        toast.success('Mouvement modifié');
      }
      setEditEntry(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const deleteEntry = async (m) => {
    if (m.source !== 'tresorerie') {
      toast.info(`Cette ligne provient de ${SOURCE_BADGE[m.source]?.label}. Supprimez-la depuis sa source.`);
      return;
    }
    if (!window.confirm(`Supprimer définitivement "${m.description}" (${fmt(m.montant)} FCFA) ?`)) return;
    try {
      await axios.delete(`${API}/api/financial/${m.type}/${m.id}`);
      toast.success('Mouvement supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  };

  return (
    <div className="space-y-5" data-testid="compte-bancaire-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack} className="gap-1" data-testid="compte-bancaire-back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Landmark className="h-6 w-6 text-cyan-600" /> Compte Bancaire
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs">Année</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-24" data-testid="compte-bancaire-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => openAdd('credit')} className="bg-emerald-600 hover:bg-emerald-700 gap-2" size="sm" data-testid="add-entree-btn">
            <Plus className="h-4 w-4" /> Entrée
          </Button>
          <Button onClick={() => openAdd('debit')} className="bg-rose-600 hover:bg-rose-700 gap-2" size="sm" data-testid="add-sortie-btn">
            <Plus className="h-4 w-4" /> Sortie
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500 bg-emerald-50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-emerald-700 font-semibold uppercase">Entrées</p>
              <p className="text-2xl font-bold text-emerald-700">{fmt(data.total_entrees)} FCFA</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-rose-500 bg-rose-50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-rose-500 text-white flex items-center justify-center">
              <TrendingDown className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-rose-700 font-semibold uppercase">Sorties</p>
              <p className="text-2xl font-bold text-rose-700">{fmt(data.total_sorties)} FCFA</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${data.solde_courant >= 0 ? 'border-l-cyan-500 bg-cyan-50' : 'border-l-orange-500 bg-orange-50'}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${data.solde_courant >= 0 ? 'bg-cyan-500' : 'bg-orange-500'} text-white flex items-center justify-center`}>
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <p className={`text-xs ${data.solde_courant >= 0 ? 'text-cyan-700' : 'text-orange-700'} font-semibold uppercase`}>Solde courant</p>
              <p className={`text-2xl font-bold ${data.solde_courant >= 0 ? 'text-cyan-700' : 'text-orange-700'}`}>
                {fmt(data.solde_courant)} FCFA
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tableau bilatéral Entrées | Sorties */}
      <Card>
        <CardContent className="p-0 overflow-hidden">
          <div className="grid grid-cols-2 border-b border-slate-200">
            <div className="bg-emerald-600 text-white text-center py-3 font-bold tracking-wide flex items-center justify-center gap-2">
              <ArrowDownLeft className="h-4 w-4" /> ENTRÉES (CRÉDITS)
            </div>
            <div className="bg-rose-600 text-white text-center py-3 font-bold tracking-wide flex items-center justify-center gap-2">
              <ArrowUpRight className="h-4 w-4" /> SORTIES (DÉBITS)
            </div>
          </div>
          {loading ? (
            <div className="text-center py-12 text-slate-500">Chargement…</div>
          ) : (data.mouvements || []).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Landmark className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              Aucun mouvement bancaire pour {year}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {grouped.map(([month, items]) => (
                <div key={month}>
                  <div className="bg-slate-100 px-4 py-2 text-xs text-slate-700 uppercase tracking-wide font-semibold flex items-center justify-between">
                    <span className="capitalize">{monthLabel(month)}</span>
                    <span className="text-slate-500 normal-case">{items.length} mouvement{items.length > 1 ? 's' : ''}</span>
                  </div>
                  {items.map((m) => (
                    <div key={m.id} className="grid grid-cols-2 hover:bg-slate-50" data-testid={`mvt-row-${m.id}`}>
                      <div className={`p-3 border-r border-slate-100 ${m.type !== 'credit' ? 'opacity-30' : ''}`}>
                        {m.type === 'credit' && (
                          <Mouvement m={m} accent="emerald" onEdit={() => openEdit(m)} onDelete={() => deleteEntry(m)} />
                        )}
                      </div>
                      <div className={`p-3 ${m.type !== 'debit' ? 'opacity-30' : ''}`}>
                        {m.type === 'debit' && (
                          <Mouvement m={m} accent="rose" onEdit={() => openEdit(m)} onDelete={() => deleteEntry(m)} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modale Ajout/Édition */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editEntry?.mode === 'add'
                ? (editEntry.sens === 'credit' ? 'Ajouter une entrée' : 'Ajouter une sortie')
                : 'Modifier le mouvement'}
            </DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={editEntry.date}
                  onChange={(e) => setEditEntry({ ...editEntry, date: e.target.value })}
                  data-testid="mvt-date"
                />
              </div>
              <div>
                <Label className="text-xs">Description *</Label>
                <Input
                  value={editEntry.description}
                  onChange={(e) => setEditEntry({ ...editEntry, description: e.target.value })}
                  placeholder={editEntry.sens === 'credit' ? 'Ex: Vente campagne client X' : 'Ex: Loyer juin 2026'}
                  data-testid="mvt-description"
                />
              </div>
              <div>
                <Label className="text-xs">Catégorie</Label>
                <Select
                  value={editEntry.categorie}
                  onValueChange={(v) => setEditEntry({ ...editEntry, categorie: v })}
                >
                  <SelectTrigger data-testid="mvt-categorie">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(editEntry.sens === 'credit' ? CATEGORIES_CREDIT : CATEGORIES_DEBIT).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Montant (FCFA) *</Label>
                <Input
                  type="number"
                  value={editEntry.montant}
                  onChange={(e) => setEditEntry({ ...editEntry, montant: e.target.value })}
                  placeholder="0"
                  data-testid="mvt-montant"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Annuler</Button>
            <Button
              onClick={submitEntry}
              className={editEntry?.sens === 'credit' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              data-testid="mvt-submit"
            >
              {editEntry?.mode === 'add' ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Mouvement({ m, accent, onEdit, onDelete }) {
  const badge = SOURCE_BADGE[m.source] || SOURCE_BADGE.tresorerie;
  const isReadOnly = m.source !== 'tresorerie';
  return (
    <div className="flex items-start justify-between gap-2 group">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-mono">{fmtDate(m.date)}</span>
          <Badge variant="outline" className={`text-[10px] ${badge.cls}`}>{badge.label}</Badge>
          {isReadOnly && <Lock className="h-2.5 w-2.5 text-slate-400" />}
        </div>
        <p className="text-sm font-semibold text-slate-900 mt-1 truncate" title={m.description}>{m.description}</p>
        <p className="text-[11px] text-slate-500 truncate">{m.categorie}</p>
      </div>
      <div className="flex items-start gap-1 flex-shrink-0">
        <div className="text-right">
          <p className={`text-sm font-bold text-${accent}-700`}>
            {accent === 'emerald' ? '+' : '−'}{fmt(m.montant)}
          </p>
          <p className="text-[10px] text-slate-400">Solde: {fmt(m.solde_apres)}</p>
        </div>
        {!isReadOnly && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-0.5">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onEdit} data-testid={`edit-mvt-${m.id}`} title="Modifier">
              <Pencil className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600 hover:bg-rose-50" onClick={onDelete} data-testid={`delete-mvt-${m.id}`} title="Supprimer">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
